import { forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import { LuChevronDown } from 'react-icons/lu';

const Section = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Header = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 14px;
	cursor: pointer;
	user-select: none;
	transition: background 0.15s;
	width: 100%;
	border: none;
	background: none;
	font: inherit;
	color: inherit;
	text-align: left;

	&:hover {
		background: rgba(152, 152, 152, 0.05);
	}

	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.green};
		outline-offset: -2px;
	}
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Icon = styled.span`
	font-size: 14px;
	width: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Title = styled.h3`
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.8px;
	color: ${({ theme }) => theme.colors.green};
	font-weight: 600;
	margin: 0;
`;

const Chevron = styled.span`
	font-size: 12px;
	color: ${({ theme }) => theme.colors.textAccent};
	transition: transform 0.25s;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Body = styled.div`
	padding: 0 14px 12px;
	display: ${({ $open }) => ($open ? 'block' : 'none')};
`;

const SidebarSection = forwardRef(({ icon, title, defaultOpen = true, children }, ref) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	useImperativeHandle(ref, () => ({
		get isOpen() {
			return isOpen;
		},
		close: () => setIsOpen(false),
		open: () => setIsOpen(true),
		setOpen: val => setIsOpen(val),
	}));

	return (
		<Section>
			<Header
				onClick={() => setIsOpen(prev => !prev)}
				data-tooltip={title}
				aria-expanded={isOpen}>
				<HeaderLeft>
					<Icon>{icon}</Icon>
					<Title>{title}</Title>
				</HeaderLeft>
				<Chevron $open={isOpen}>
					<LuChevronDown />
				</Chevron>
			</Header>
			<Body $open={isOpen}>{children}</Body>
		</Section>
	);
});

SidebarSection.displayName = 'SidebarSection';

export default SidebarSection;
